import { get as apiGet, TYPE_BROOKLYN } from 'api-client'
import _assign from 'lodash/assign'
import _cloneDeep from 'lodash/cloneDeep'
import _get from 'lodash/get'
import _reduce from 'lodash/reduce'
import _isObject from 'lodash/isObject'
import { Map, List } from 'immutable'
import {
  GEO_AVAILABILITY_BLOCKED_IN,
  GEO_AVAILABILITY_ALLOWED_IN,
} from 'services/geo-restrictions'
import { EN } from 'services/languages/constants'
import { get as getConfig } from 'config'

const MEDIA_IS_FREE = 'free'
const MEDIA_ONLY_WITH = 'onlyWith'

const SCHEMA = {
  id: null,
  _dataError: null,
  bcHLS: null,
  mediaLang: null,
  duration: 0,
  byLang: {},
  byLangDuration: {},
  textTracks: {
    captions: {},
    subtitles: {},
  },
}

const config = getConfig()
const HLS_VERSION = _get(config, ['brightcove', 'hlsversion'])
const BYTE_RANGE_DELIVERY = _get(config, ['brightcove', 'byterangedelivery'])

/**
 * Returns true when the media state is processing the requsted media id
 * @param {Object} options The options
 * @param {Map} options.media The media state
 * @param {Number} options.mediaId The media id
 * @returns {Boolean} True when the media id is processing in the media state
 */
export function getMediaIdIsProcessing (options = {}) {
  const { media = Map(), mediaId } = options
  return Map.isMap(media) && media.get('id') === mediaId && media.get('processing')
}

/**
 * Call media info endpoint to get media info for playback
 * @param {Object} options The options
 * @param {String|Number} options.id The media id
 * @param {Object|import('immutable').Map} options.auth The auth data
 * @param {import('immutable').Map} options.user The user data
 * @returns The media info
 */
export async function get (options) {
  const { id, auth, user = Map() } = options
  if (!id || id === -1) {
    return {}
  }

  try {
    const languages = user.getIn(['data', 'language']) || List()
    // add HLS version and Byte Range Delivery rule to the query params
    const queryParams = `hls=${HLS_VERSION}${BYTE_RANGE_DELIVERY ? '&byteRangeDelivery=true' : ''}`
    const res = await apiGet(`v1/media-info/${id}?${queryParams}`, { language: languages.first() || EN }, { auth }, TYPE_BROOKLYN)
    return handleResponse(res, id)
  } catch (e) {
    return handleResponse({}, id, true)
  }
}

/**
 * Handle the media info api response
 * @param {import('superagent').Response} res The api response
 * @param {Number} id The media id
 * @param {Boolean} _dataError The response was an error
 * @returns The response shaped for usage in a store
 */
function handleResponse (res, id, _dataError) {
  const data = _get(res, 'body', {})
  const byLang = _get(data, ['mediaUrls', 'byLang'])
  const byLangDuration = _get(data, ['mediaUrls', 'byLangDuration'])
  return _assign(_cloneDeep(SCHEMA), {
    id,
    _dataError,
    bcHLS: _get(data, ['mediaUrls', 'bcHLS']),
    // If byLand exist reduce it to the correct shape
    byLang: _isObject(byLang) ? _reduce(byLang, (acc, value, key) => {
      Object.assign(acc, { [key]: value })
      return acc
    }, {}) : null,
    byLangDuration: _isObject(byLangDuration) ? _reduce(byLangDuration, (acc, value, key) => {
      Object.assign(acc, { [key]: value })
      return acc
    }, {}) : null,
    duration: Number(_get(data, ['mediaUrls', 'duration']) || 0),
    mediaLang: _get(data, ['mediaUrls', 'language']) || null,
    // After 6 hours the media urls will be expired and media info needs to be refreshed
    expiresTime: Date.now() + (6 * (3600 * 1000)),
    textTracks: {
      captions: _get(data, ['textTracks', 'captions']) || {},
      subtitles: _get(data, ['textTracks', 'subtitles']) || {},
      thumbnail: _get(data, ['textTracks', 'thumbnail']) || null,
    },
    isShareable: Boolean(_get(data, 'isShareable') || false),
  })
}

/**
 * Returns true when the media is available for playback
 * @todo refactor and remove duplicates after member home test
 * @param {Object} options The options
 * @param {Map} options.media The media
 * @param {String} options.country The country where the request is being made
 * @param {List} options.userSubscriptions The user subscriptions
 */
function mediaIsAvailable (options = {}) {
  const { media, availability, userSubscriptions } = options
  if (!Map.isMap(media)) {
    return false
  }
  const subscriptions = media.getIn(['offerings', 'subscriptions']) || List()
  if (availability === MEDIA_IS_FREE) {
    return true
  }
  if (availability === MEDIA_ONLY_WITH) {
    // if user subscription doesn't match any media subscriptions, return false
    if (!subscriptions.some((v) => userSubscriptions.includes(v))) {
      return false
    }
  }
  return true
}

/**
 * Check if the media is geo restricted
 * @param {Object} options The options
 * @param {Map} options.media The media
 * @param {String} options.country The country where the request is being made
 * @returns True when the media is watchable
 */
function mediaIsGeoRestricted (options = {}) {
  const { media, country } = options
  const geoAvailability = media.getIn(['georestrictions', 'availability'], null)
  const geoCountries = media.getIn(['georestrictions', 'countries']) || List()

  if (geoAvailability === GEO_AVAILABILITY_ALLOWED_IN) {
    return !geoCountries.includes(country)
  }
  if (geoAvailability === GEO_AVAILABILITY_BLOCKED_IN) {
    return geoCountries.includes(country)
  }
  // This is for worldwide
  return false
}

/**
 * Get the watch access for the media based on the current user context
 * @param {Object} options The options
 * @param {Map} options.media The media
 * @param {String} options.country The country where the request is being made
 * @param {List} options.userSubscriptions The user subscriptions
 * @returns True when the media is watchable
 */
export function getWatchAccess (options = {}) {
  const { media = Map(), country, userSubscriptions = List() } = options
  // We don't have any media to check
  if (!Map.isMap(media)) {
    return false
  }
  const mediaId = media.get('id')
  const availability = media.getIn(['offerings', 'availability']) || null
  const isAvailable = mediaIsAvailable({
    media, availability, userSubscriptions: userSubscriptions || List(),
  })
  const isGeoRestricted = mediaIsGeoRestricted({ media, country })
  if (mediaId && isAvailable && !isGeoRestricted) {
    return true
  }
  return false
}
